/**
 * Google Tag Manager - Facet Filters Tracking
 * 
 * This script pushes filter data to the GTM dataLayer when filters are applied.
 * It listens to the 'on:facet-filters:updated' event dispatched by facet-filters.js
 */

(function() {
  'use strict';

  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];

  /**
   * Extracts filter values from the form
   * @param {HTMLFormElement} form - The filter form element
   * @returns {Object} Object containing all filter values
   */
  function extractFilterValues(form) {
    if (!form) return {};

    const formData = new FormData(form);
    const filters = {
      sortBy: null,
      availability: [],
      priceMin: null,
      priceMax: null,
      discount: null,
      productType: [],
      colour: [],
      material: [],
      sale: []
    };

    // Extract sort by
    const sortBy = formData.get('sort_by');
    if (sortBy) {
      filters.sortBy = sortBy;
    }

    // Extract availability (checkboxes - multiple values)
    const availabilityValues = formData.getAll('filter.v.m.inventory.location_availability');
    if (availabilityValues.length > 0) {
      filters.availability = availabilityValues.filter(v => v);
    }

    // Extract price range
    const priceMin = formData.get('filter.v.price.gte');
    const priceMax = formData.get('filter.v.price.lte');
    if (priceMin) filters.priceMin = priceMin;
    if (priceMax) filters.priceMax = priceMax;

    // Extract discount (radio - single value)
    const discount = formData.get('filter.v.m.custom.collection_discount_filter');
    if (discount) {
      filters.discount = discount;
    }

    // Extract product type (checkboxes - multiple values)
    const productTypeValues = formData.getAll('filter.p.product_type');
    if (productTypeValues.length > 0) {
      filters.productType = productTypeValues.filter(v => v);
    }

    // Extract colour (checkboxes - multiple values)
    const colourValues = formData.getAll('filter.p.m.custom.collection_colour_filter');
    if (colourValues.length > 0) {
      filters.colour = colourValues.filter(v => v);
    }

    // Extract material (checkboxes - multiple values)
    const materialValues = formData.getAll('filter.p.m.custom.collection_material_filter');
    if (materialValues.length > 0) {
      filters.material = materialValues.filter(v => v);
    }

    // Extract sale (checkboxes - multiple values, usually one)
    const saleValues = formData.getAll('filter.p.m.custom.collection_sale_filter');
    if (saleValues.length > 0) {
      filters.sale = saleValues.filter(v => v);
    }

    return filters;
  }

  /**
   * Calculates the total number of active filters
   * @param {Object} filters - Filter values object
   * @returns {number} Total count of active filters
   */
  function calculateFilterCount(filters) {
    let count = 0;
    
    if (filters.sortBy && filters.sortBy !== 'manual') count++;
    if (filters.availability.length > 0) count += filters.availability.length;
    if (filters.priceMin || filters.priceMax) count++;
    if (filters.discount) count++;
    if (filters.productType.length > 0) count += filters.productType.length;
    if (filters.colour.length > 0) count += filters.colour.length;
    if (filters.material.length > 0) count += filters.material.length;
    if (filters.sale.length > 0) count += filters.sale.length;

    return count;
  }

  /**
   * Creates a human-readable string of all active filters
   * @param {Object} filters - Filter values object
   * @returns {string} Human-readable filter string
   */
  function createFilterString(filters) {
    const parts = [];

    // Sort by
    if (filters.sortBy && filters.sortBy !== 'manual') {
      const sortLabels = {
        'best-selling': 'Best selling',
        'price-ascending': 'Price, low to high',
        'price-descending': 'Price, high to low',
        'created-descending': 'New Arrivals',
        'manual': 'Featured'
      };
      parts.push(`Sort: ${sortLabels[filters.sortBy] || filters.sortBy}`);
    }

    // Availability
    if (filters.availability.length > 0) {
      parts.push(`Availability: ${filters.availability.join(', ')}`);
    }

    // Price range
    if (filters.priceMin || filters.priceMax) {
      const min = filters.priceMin ? `£${filters.priceMin}` : '';
      const max = filters.priceMax ? `£${filters.priceMax}` : '';
      if (min && max) {
        parts.push(`Price: ${min}-${max}`);
      } else if (min) {
        parts.push(`Price: ${min}+`);
      } else if (max) {
        parts.push(`Price: up to ${max}`);
      }
    }

    // Discount
    if (filters.discount) {
      parts.push(`Discount: ${filters.discount}`);
    }

    // Product type
    if (filters.productType.length > 0) {
      parts.push(`Product Type: ${filters.productType.join(', ')}`);
    }

    // Colour
    if (filters.colour.length > 0) {
      parts.push(`Colour: ${filters.colour.join(', ')}`);
    }

    // Material
    if (filters.material.length > 0) {
      parts.push(`Material: ${filters.material.join(', ')}`);
    }

    // Sale
    if (filters.sale.length > 0) {
      parts.push(`Sale: ${filters.sale.join(', ')}`);
    }

    return parts.length > 0 ? parts.join(' | ') : 'No filters';
  }

  /**
   * Pushes filter data to the GTM dataLayer
   * @param {Object} filters - Filter values object
   */
  function pushToDataLayer(filters) {
    const filterCount = calculateFilterCount(filters);
    const filterString = createFilterString(filters);

    // Format arrays as comma-separated strings for GTM (or keep as arrays)
    // GTM can handle arrays, but strings are sometimes easier to work with
    const dataLayerObject = {
      event: 'filter_applied',
      filterEvent: 'filter_applied',
      filterSortBy: filters.sortBy || null,
      filterAvailability: filters.availability.length > 0 ? filters.availability : null,
      filterPriceMin: filters.priceMin || null,
      filterPriceMax: filters.priceMax || null,
      filterDiscount: filters.discount || null,
      filterProductType: filters.productType.length > 0 ? filters.productType : null,
      filterColour: filters.colour.length > 0 ? filters.colour : null,
      filterMaterial: filters.material.length > 0 ? filters.material : null,
      filterSale: filters.sale.length > 0 ? filters.sale : null,
      filterCount: filterCount,
      filterAllFilters: filterString
    };

    // Push to dataLayer
    window.dataLayer.push(dataLayerObject);

    // Debug logging (remove in production if desired)
    if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
      console.log('GTM Filter Tracking:', dataLayerObject);
    }
  }

  /**
   * Gets the current active filter count
   * @returns {number} Number of active filters
   */
  function getCurrentFilterCount() {
    const form = document.getElementById('facets');
    if (!form) return 0;

    const filters = extractFilterValues(form);
    return calculateFilterCount(filters);
  }

  /**
   * Determines if filters are currently open
   * @returns {boolean} True if filters are open, false otherwise
   */
  function areFiltersOpen() {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    
    if (isMobile) {
      // On mobile, check if the drawer is open
      const facetFilters = document.querySelector('facet-filters');
      return facetFilters && facetFilters.hasAttribute('open');
    } else {
      // On desktop, check if the filter button is active
      const filterToggle = document.querySelector('.js-toggle-filters');
      return filterToggle && filterToggle.classList.contains('is-active');
    }
  }

  /**
   * Tracks when the filter button is clicked
   * @param {Event} event - Click event
   */
  function trackFilterButtonClick(event) {
    // Determine if this is opening or closing
    const wasOpen = areFiltersOpen();
    const activeFilterCount = getCurrentFilterCount();
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;

    // Small delay to allow the state to change
    setTimeout(() => {
      const isNowOpen = areFiltersOpen();
      const action = isNowOpen ? 'opened' : 'closed';

      const dataLayerObject = {
        event: 'filter_button_clicked',
        filterButtonEvent: 'filter_button_clicked',
        filterButtonAction: action, // 'opened' or 'closed'
        filterButtonDevice: isMobile ? 'mobile' : 'desktop',
        filterButtonActiveCount: activeFilterCount,
        filterButtonWasOpen: wasOpen,
        filterButtonIsNowOpen: isNowOpen
      };

      // Push to dataLayer
      window.dataLayer.push(dataLayerObject);

      // Debug logging
      if (window.location.hostname === 'localhost' || window.location.search.includes('debug=true')) {
        console.log('GTM Filter Button Click:', dataLayerObject);
      }
    }, 50);
  }

  /**
   * Sets up filter button click tracking
   */
  function setupFilterButtonTracking() {
    // Track desktop filter button
    const filterToggle = document.querySelector('.js-toggle-filters');
    if (filterToggle) {
      filterToggle.addEventListener('click', trackFilterButtonClick);
    }

    // Track mobile filter button (#enable_filters)
    const mobileFilterButton = document.getElementById('enable_filters');
    if (mobileFilterButton) {
      mobileFilterButton.addEventListener('click', trackFilterButtonClick);
    }
    console.log("clicked");



    // Also track clicks on the parent button wrapper on mobile
    const filterButtonWrapper = document.querySelector('.products-toolbar__filter-btn');
    if (filterButtonWrapper) {
      filterButtonWrapper.addEventListener('click', function(event) {
        // Only track if clicking directly on the button or its children (but not on sort button)
        const clickedElement = event.target;
        const isSortButton = clickedElement.closest('#enable_sort_by');
        if (!isSortButton) {
          trackFilterButtonClick(event);
        }
      });
    }
  }

  /**
   * Handles the filter update event
   */
  function handleFilterUpdate() {
    // Wait a brief moment for the DOM to update
    setTimeout(() => {
      const form = document.getElementById('facets');
      if (!form) return;

      const filters = extractFilterValues(form);
      pushToDataLayer(filters);
    }, 100);
  }

  // Listen for the filter update event
  document.addEventListener('on:facet-filters:updated', handleFilterUpdate);

  // Set up filter button click tracking
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupFilterButtonTracking();
      
      // Check if there are any active filters on page load
      const form = document.getElementById('facets');
      if (form) {
        const urlParams = new URLSearchParams(window.location.search);
        const hasFilters = Array.from(urlParams.keys()).some(key => 
          key.startsWith('filter.') || key === 'sort_by'
        );
        
        if (hasFilters) {
          // Small delay to ensure form is fully populated
          setTimeout(handleFilterUpdate, 500);
        }
      }
    });
  } else {
    // DOM already loaded
    setupFilterButtonTracking();
    
    const form = document.getElementById('facets');
    if (form) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasFilters = Array.from(urlParams.keys()).some(key => 
        key.startsWith('filter.') || key === 'sort_by'
      );
      
      if (hasFilters) {
        setTimeout(handleFilterUpdate, 500);
      }
    }
  }
})();

